import i18n from 'i18next';

import type { StreamGroupView } from './EventApi';

interface StreamGroupInput {
  group_id?: string; group_name?: string;
}
interface StreamInput {
  stream_id?: string; name?: string; groups?: string[];
}
interface StreamItem {
  name: string;
}

/** Идентификатор синтетической секции «Источники без группы». */
const UNGROUPED_ID = '__ungrouped__';

interface GroupedStreams {
  /** Секции по группам бэкенда (в том же порядке), уже с элементами. */
  views: StreamGroupView[];
  /** Потоки без единой группы. */
  ungrouped: StreamItem[];
}

/**
 * Ядро группировки потоков для фильтра источников (без платформенных нюансов).
 *
 * `stream.groups` может содержать как id, так и имя группы — объединяем оба
 * набора, чтобы не терять потоки, сгруппированные по имени.
 */
const groupStreams = (groups: StreamGroupInput[], streams: StreamInput[]): GroupedStreams => {
  const knownKeys = new Set<string>();
  groups.forEach((group) => {
    if (group.group_id) knownKeys.add(group.group_id);
    if (group.group_name) knownKeys.add(group.group_name);
  });

  const byGroupKey = new Map<string, StreamItem[]>();
  const ungrouped: StreamItem[] = [];

  streams.forEach((stream) => {
    const item = { name: stream.name ?? stream.stream_id ?? '' };
    const rawGroups = stream.groups ?? [];

    if (rawGroups.length === 0) {
      ungrouped.push(item);
      return;
    }

    rawGroups
      .filter((key) => knownKeys.has(key))
      .forEach((key) => {
        const bucket = byGroupKey.get(key);

        if (bucket) bucket.push(item);
        else byGroupKey.set(key, [item]);
      });
  });

  const views = groups.map((group) => {
    const itemsByName = new Map<string, StreamItem>();
    [group.group_id, group.group_name].forEach((key) => {
      if (!key) return;
      (byGroupKey.get(key) ?? []).forEach((item) => itemsByName.set(item.name, item));
    });

    return {
      id: group.group_id ?? '',
      label: group.group_name ?? '',
      items: [...itemsByName.values()].map((s) => ({ value: s.name, label: s.name })),
    };
  });

  return { views, ungrouped };
};

/**
 * Группы потоков для Luna/Point (legacy): потоки без группы допустимы и
 * показываются отдельной секцией «Источники без группы».
 */
const buildLunaStreamGroups = (
  groups: StreamGroupInput[],
  streams: StreamInput[],
): StreamGroupView[] => {
  const { views, ungrouped } = groupStreams(groups, streams);

  if (ungrouped.length > 0) {
    views.push({
      id: UNGROUPED_ID,
      label: i18n.t('sources.ungrouped sources title'),
      items: ungrouped.map((s) => ({ value: s.name, label: s.name })),
    });
  }

  return views.filter((group) => group.items.length > 0);
};

/**
 * Группы потоков для Point3: поток всегда принадлежит группе, поэтому потоки
 * без группы (пустой `groups` или ссылка на несуществующую группу) не показываем.
 */
const buildArtemyStreamGroups = (
  groups: StreamGroupInput[],
  streams: StreamInput[],
): StreamGroupView[] => groupStreams(groups, streams).views.filter((group) => group.items.length > 0);

export { buildLunaStreamGroups, buildArtemyStreamGroups };
